import { AvatarSize, Avatar, DataGrid, TableCell, theme, Icon } from '@ketch-com/deck'
import {
  GetConsentStatusResponseBodySubscriptionsItemDTO,
  PermitTransactionV2OrchestrationsDTO,
} from '@ketch-com/figurehead'
import { Box, Typography } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { NoDataEmptyState } from 'pages/insights/auditLog/components/NoDataEmptyState'
import { PermitResultStatus } from 'pages/insights/auditLog/components/PermitResultStatus'
import { useAuditLogContext } from 'pages/insights/auditLog/context/AuditLogContext'
import { useMemo } from 'react'

type ChannelConnectionsProps = {
  channel: GetConsentStatusResponseBodySubscriptionsItemDTO
}

const columns: GridColDef<PermitTransactionV2OrchestrationsDTO>[] = [
  {
    align: 'left',
    field: 'identifier',
    width: 500,
    headerAlign: 'left',
    headerName: 'System',
    renderHeader: params => <Typography variant="smallLabel">{params.colDef.headerName}</Typography>,
    sortable: false,
    renderCell: ({ row: connection }: GridRenderCellParams<PermitTransactionV2OrchestrationsDTO>) => {
      return (
        <Box display="flex" alignItems="center" gap={1} py={1}>
          <Avatar
            src={connection.logoURL}
            size={AvatarSize.medium}
            variant="circular"
            borderColor={theme.palette.Common.Divider}
            sx={{ p: 0.75 }}
          />
          <Box display="flex" flexDirection="column">
            <Typography variant="label">{connection.dataSystemName}</Typography>
          </Box>
        </Box>
      )
    },
  },
  {
    align: 'left',
    field: 'connection',
    width: 268,
    headerAlign: 'left',
    headerName: 'Connection',
    renderHeader: params => <Typography variant="smallLabel">{params.colDef.headerName}</Typography>,
    sortable: false,
    renderCell: ({ row: connection }: GridRenderCellParams<PermitTransactionV2OrchestrationsDTO>) => {
      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Icon name="OConnectionTrue" width={20} height={20} iconColor={theme.palette.Success.PrimaryDark} />
          <Typography variant="smallLabel" color={theme.palette.Success.PrimaryDark}>
            {connection.connectionName}
          </Typography>
        </Box>
      )
    },
  },
  {
    align: 'left',
    field: 'result',
    flex: 2,
    headerAlign: 'left',
    headerName: 'Result',
    renderHeader: params => <Typography variant="smallLabel">{params.colDef.headerName}</Typography>,
    sortable: false,
    renderCell: ({ row: connection }: GridRenderCellParams<PermitTransactionV2OrchestrationsDTO>) => {
      return (
        <TableCell>
          <PermitResultStatus result={connection.result} />
        </TableCell>
      )
    },
  },
]

export const ChannelConnections: React.FC<ChannelConnectionsProps> = ({ channel }) => {
  const { subscriptionTransactionData } = useAuditLogContext()

  const transaction = subscriptionTransactionData.find(t => t.transactionID === channel.transactionID)
  const rows = useMemo(() => {
    // Sort connection by systemName, or systemCode if systemName is not available
    return Object.values(transaction?.orchestrations || {})
      .filter(connection => !!connection.connectionID)
      .sort((a, b) => {
        if (a.dataSystemName && b.dataSystemName) {
          return a.dataSystemName.localeCompare(b.dataSystemName)
        }
        return a.connectionCode?.localeCompare(b.connectionCode || '') || 0
      })
  }, [transaction?.orchestrations])
  const rowCount = rows.length
  const showPagination = rowCount > 5

  return rowCount > 0 ? (
    <DataGrid
      autosizeOnMount
      autosizeOptions={{
        expand: true,
        includeHeaders: true,
        includeOutliers: false,
      }}
      getRowHeight={() => 'auto'}
      getRowId={(row: PermitTransactionV2OrchestrationsDTO) => row.connectionCode || ''}
      columns={columns}
      rows={rows}
      checkboxSelection={false}
      disableBorder
      disableRowSelectionOnClick
      disableColumnMenu
      disableColumnPinning
      disableColumnReorder
      disableColumnResize
      disableChildrenSorting={false}
      disableRowHoverStates
      paginationMode="client"
      rowCount={rows.length}
      pagination={showPagination}
      hideFooter={!showPagination}
      pageSizeOptions={[5, 10, 20]}
      noRowsOverlayProps={{ buttonTitle: '' }}
      sx={{
        '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox):hover': {
          backgroundImage: 'unset',
          cursor: 'auto',
        },
        p: 0,
      }}
    />
  ) : (
    <NoDataEmptyState
      title="No System Data"
      subtitle="We couldn't find any system orchestration data for this communication channel. This may be due to the subscription having just been created, or it was created before orchestration data was tracked."
    />
  )
}
